import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  Shield,
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  ArrowLeft,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "../../hooks/useAdminAuth";
import { useAppSelector } from "@/hooks/redux";
import adminService from "@/services/adminService";
import { AuthStickyControls } from "@/components/auth/AuthStickyControls";
import { Formik, Form, Field, ErrorMessage } from "formik";
import {
  forgotPasswordSchema,
  newPasswordSchema,
} from "../../schemas/adminSchemas";

interface AdminCredentials {
  email: string;
  password: string;
}

interface FirstAdminData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

type AuthStep =
  | "checking"
  | "first-admin-setup"
  | "first-admin-otp"
  | "credentials"
  | "otp"
  | "forgot-password"
  | "reset-otp"
  | "new-password";

export const AdminLogin: React.FC = () => {
  const { t } = useTranslation();

  // Form states
  const [credentials, setCredentials] = useState<AdminCredentials>({
    email: "",
    password: "",
  });
  const [firstAdminData, setFirstAdminData] = useState<FirstAdminData>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [otpCode, setOtpCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [authStep, setAuthStep] = useState<AuthStep>("checking");
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [firstAdminOtp, setFirstAdminOtp] = useState("");

  // UI states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const {
    verifyCredentialsAndRequestOTP,
    verifyOTPAndSignIn,
    requestPasswordReset,
    verifyPasswordResetOTP,
    checkAdminSession,
    currentEmail,
    resetEmail,
  } = useAdminAuth();

  const navigate = useNavigate();

  // Get admin state from Redux for immediate check
  const { isAuthenticated: isAdminAuthenticated } = useAppSelector(
    (state) => state.admin,
  );

  // Check if admin is already authenticated on component mount
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        setIsCheckingSession(true);

        // First check Redux state for quick response
        if (isAdminAuthenticated) {
          // Verify with backend to be sure
          const isSessionValid = await checkAdminSession();
          if (isSessionValid) {
            toast.success(t("admin.login.sessionActive"));
            navigate("/admin/dashboard", { replace: true });
            return;
          }
        } else {
          // Check with backend even if Redux says not authenticated
          // (user might have logged in from another tab)
          const isSessionValid = await checkAdminSession();
          if (isSessionValid) {
            toast.success(t("admin.login.sessionDetected"));
            navigate("/admin/dashboard", { replace: true });
            return;
          }
        }

        // No valid session, check if any admin exists
        const adminExistsResult = await adminService.checkAdminExists();

        if (!adminExistsResult.hasAdmins) {
          setAuthStep("first-admin-setup");
        } else {
          setAuthStep("credentials");
        }
      } catch (error) {
        console.error("Erreur lors de la vérification de la session:", error);
        // If we can't check admin existence, assume normal login
        setAuthStep("credentials");
      } finally {
        setIsCheckingSession(false);
      }
    };

    checkExistingSession();
  }, [checkAdminSession, navigate, isAdminAuthenticated]);

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!credentials.email || !credentials.password) {
      toast.error(t("admin.validation.fillAllFields"));
      return;
    }

    try {
      setIsLoading(true);
      const result = await verifyCredentialsAndRequestOTP(credentials);
      if (result.success) {
        setAuthStep("otp");
        toast.success(t("admin.login.verificationSent"));
        // In development, show the OTP for testing
        if (result.otp) {
          toast.info(t("admin.login.otpDevMode", { otp: result.otp }));
        }
      }
    } catch (error) {
      console.error("Erreur de vérification des identifiants:", error);
      toast.error(t("admin.login.verificationFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode || !currentEmail) {
      toast.error(t("admin.validation.enterOtp"));
      return;
    }

    try {
      setIsLoading(true);
      await verifyOTPAndSignIn({ email: currentEmail, otpCode });
      toast.success(t("admin.login.loginSuccess"));
      navigate("/admin/dashboard");
    } catch (error) {
      console.error("Erreur de vérification OTP:", error);
      toast.error(t("admin.login.otpInvalid"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (email: string) => {
    if (!email) {
      toast.error(t("admin.validation.enterEmail"));
      return;
    }

    try {
      setIsLoading(true);
      const result = await requestPasswordReset(email);
      if (result.success) {
        setAuthStep("reset-otp");
        toast.success(t("admin.login.resetSent"));
        // In development, show the OTP for testing
        if (result.otp) {
          toast.info(t("admin.login.otpDevMode", { otp: result.otp }));
        }
      }
    } catch (error) {
      console.error("Erreur de demande de réinitialisation:", error);
      toast.error(t("admin.login.resetError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetOtp || !resetEmail) {
      toast.error(t("admin.validation.enterOtp"));
      return;
    }

    setAuthStep("new-password");
  };

  const handleNewPasswordSubmit = async (
    newPasswordValue: string,
    confirmPasswordValue: string,
  ) => {
    if (
      !newPasswordValue ||
      !confirmPasswordValue ||
      !resetOtp ||
      !resetEmail
    ) {
      toast.error(t("admin.validation.fillAllFields"));
      return;
    }

    if (newPasswordValue !== confirmPasswordValue) {
      toast.error(t("admin.validation.passwordMismatch"));
      return;
    }

    if (newPasswordValue.length < 8) {
      toast.error(t("admin.validation.passwordTooShort"));
      return;
    }

    try {
      setIsLoading(true);
      await verifyPasswordResetOTP({
        email: resetEmail,
        otp: resetOtp,
        newPassword: newPasswordValue,
      });
      toast.success(t("admin.login.passwordResetSuccess"));
      setAuthStep("credentials");
      // Reset form
      setCredentials({ email: resetEmail, password: "" });
      setForgotEmail("");
      setResetOtp("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      console.error("Erreur de réinitialisation du mot de passe:", error);
      toast.error(t("admin.login.passwordResetError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFirstAdminSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !firstAdminData.firstName ||
      !firstAdminData.lastName ||
      !firstAdminData.email ||
      !firstAdminData.password ||
      !firstAdminData.confirmPassword
    ) {
      toast.error(t("admin.validation.fillAllFields"));
      return;
    }

    if (firstAdminData.password !== firstAdminData.confirmPassword) {
      toast.error(t("admin.validation.passwordMismatch"));
      return;
    }

    if (firstAdminData.password.length < 8) {
      toast.error(t("admin.validation.passwordTooShort"));
      return;
    }

    try {
      setIsLoading(true);
      const result = await adminService.requestFirstAdminOTP({
        firstName: firstAdminData.firstName,
        lastName: firstAdminData.lastName,
        email: firstAdminData.email,
        password: firstAdminData.password,
      });

      if (result.success) {
        setAuthStep("first-admin-otp");
        toast.success(t("admin.login.verificationSent"));
        // In development, show the OTP for testing
        if (result.otp) {
          toast.info(t("admin.login.otpDevMode", { otp: result.otp }));
        }
      }
    } catch (error) {
      console.error(
        "Erreur lors de la demande d'OTP pour le premier admin:",
        error,
      );
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error(t("admin.login.verificationFailed"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFirstAdminOTPSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstAdminOtp || !firstAdminData.email) {
      toast.error(t("admin.validation.enterOtp"));
      return;
    }

    try {
      setIsLoading(true);
      await adminService.verifyFirstAdminOTP(
        firstAdminData.email,
        firstAdminOtp,
      );

      toast.success(t("admin.setup.createSuccess"));

      // Set credentials for automatic login
      setCredentials({
        email: firstAdminData.email,
        password: firstAdminData.password,
      });

      // Switch to credentials step for login
      setAuthStep("credentials");

      // Clear first admin form and OTP
      setFirstAdminData({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
      });
      setFirstAdminOtp("");
    } catch (error) {
      console.error("Erreur lors de la vérification OTP premier admin:", error);
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error(t("admin.setup.verifyOtpError"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetToCredentials = () => {
    setAuthStep("credentials");
    setOtpCode("");
    setForgotEmail("");
    setResetOtp("");
    setNewPassword("");
    setConfirmPassword("");
  };

  // Show loading spinner while checking session
  if (isCheckingSession) {
    return (
      <div className="min-h-screen ">
        <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
          <Card className="w-full max-w-md glass-effect border-0 shadow-2xl">
            <CardContent className="p-8">
              <div className="text-center py-8">
                <div className="relative mb-6">
                  <div className="absolute inset-0 animate-ping h-12 w-12 border-4 border-primary/50 rounded-full mx-auto"></div>
                  <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
                </div>
                <div className="space-y-2">
                  <h3 className="text-xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                    {t("admin.login.checking")}
                  </h3>
                  <p className="text-muted-foreground">
                    {t("admin.loading.wait")}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const getStepTitle = () => {
    switch (authStep) {
      case "checking":
        return t("admin.login.checking");
      case "first-admin-setup":
        return t("admin.setup.title");
      case "first-admin-otp":
        return t("admin.setup.otpTitle");
      case "credentials":
        return t("admin.login.title");
      case "otp":
        return t("admin.otp.title");
      case "forgot-password":
        return t("admin.forgotPassword.title");
      case "reset-otp":
        return t("admin.resetOtp.title");
      case "new-password":
        return t("admin.newPassword.title");
      default:
        return t("admin.title");
    }
  };

  const getStepDescription = () => {
    switch (authStep) {
      case "checking":
        return t("admin.login.checkingSubtitle");
      case "first-admin-setup":
        return t("admin.setup.subtitle");
      // case "first-admin-otp":
      //   return t("admin.setup.otpSubtitle");
      case "credentials":
        return t("admin.login.subtitle");
      // case "otp":
      //   return t("admin.otp.subtitle");
      case "forgot-password":
        return t("admin.forgotPassword.subtitle");
      // case "reset-otp":
      //   return t("admin.resetOtp.subtitle");
      case "new-password":
        return t("admin.newPassword.subtitle");
      default:
        return "";
    }
  };

  const renderFirstAdminSetupForm = () => (
    <form onSubmit={handleFirstAdminSubmit} className="space-y-6">
      {/* Personal Information */}
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label
              htmlFor="firstName"
              className="flex items-center gap-2 text-sm font-medium"
            >
              <User className="h-4 w-4" />
              {t("admin.form.firstName")}
            </Label>
            <Input
              id="firstName"
              type="text"
              value={firstAdminData.firstName}
              onChange={(e) =>
                setFirstAdminData((prev) => ({
                  ...prev,
                  firstName: e.target.value,
                }))
              }
              placeholder={t("admin.form.placeholder.firstName")}
              className="transition-all duration-200 focus:scale-[1.02]"
              required
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="lastName"
              className="flex items-center gap-2 text-sm font-medium"
            >
              <User className="h-4 w-4" />
              {t("admin.form.lastName")}
            </Label>
            <Input
              id="lastName"
              type="text"
              value={firstAdminData.lastName}
              onChange={(e) =>
                setFirstAdminData((prev) => ({
                  ...prev,
                  lastName: e.target.value,
                }))
              }
              placeholder={t("admin.form.placeholder.lastName")}
              className="transition-all duration-200 focus:scale-[1.02]"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="adminEmail"
            className="flex items-center gap-2 text-sm font-medium"
          >
            <Mail className="h-4 w-4" />
            {t("admin.form.email")}
          </Label>
          <Input
            id="adminEmail"
            type="email"
            value={firstAdminData.email}
            onChange={(e) =>
              setFirstAdminData((prev) => ({ ...prev, email: e.target.value }))
            }
            placeholder={t("admin.form.placeholder.email")}
            className="transition-all duration-200 focus:scale-[1.02]"
            required
          />
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="adminPassword"
            className="flex items-center gap-2 text-sm font-medium"
          >
            <Lock className="h-4 w-4" />
            {t("admin.form.password")}
          </Label>
          <div className="relative">
            <Input
              id="adminPassword"
              type={showPassword ? "text" : "password"}
              value={firstAdminData.password}
              onChange={(e) =>
                setFirstAdminData((prev) => ({
                  ...prev,
                  password: e.target.value,
                }))
              }
              placeholder={t("admin.form.placeholder.password")}
              className="pr-10 transition-all duration-200 focus:scale-[1.02]"
              required
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="confirmAdminPassword"
            className="flex items-center gap-2 text-sm font-medium"
          >
            <Lock className="h-4 w-4" />
            {t("admin.form.confirmPassword")}
          </Label>
          <div className="relative">
            <Input
              id="confirmAdminPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={firstAdminData.confirmPassword}
              onChange={(e) =>
                setFirstAdminData((prev) => ({
                  ...prev,
                  confirmPassword: e.target.value,
                }))
              }
              placeholder={t("admin.form.placeholder.password")}
              className="pr-10 transition-all duration-200 focus:scale-[1.02]"
              required
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>
        </div>
      </div>

      <Button
        type="submit"
        disabled={isLoading}
        className="w-full h-12 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-300 shadow-lg hover:shadow-xl group"
      >
        {isLoading ? (
          <div className="flex items-center justify-center gap-2">
            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
            <span>{t("admin.loading.creating")}</span>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <CheckCircle className="h-4 w-4 group-hover:scale-110 transition-transform" />
            <span>{t("admin.setup.createButton")}</span>
          </div>
        )}
      </Button>
    </form>
  );

  const renderFirstAdminOTPForm = () => (
    <form onSubmit={handleFirstAdminOTPSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <div className="p-3 bg-primary/10 rounded-full">
              <Mail className="h-6 w-6 text-primary" />
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("admin.setup.checkEmail", { email: firstAdminData.email })}
          </p>
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="firstAdminOtp"
            className="flex items-center justify-center gap-2 text-sm font-medium"
          >
            <Shield className="h-4 w-4" />
            {t("admin.form.verificationCode")}
          </Label>
          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={firstAdminOtp}
              onChange={setFirstAdminOtp}
              className="gap-2"
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} className="w-12 h-12 text-lg" />
                <InputOTPSlot index={1} className="w-12 h-12 text-lg" />
                <InputOTPSlot index={2} className="w-12 h-12 text-lg" />
                <InputOTPSlot index={3} className="w-12 h-12 text-lg" />
                <InputOTPSlot index={4} className="w-12 h-12 text-lg" />
                <InputOTPSlot index={5} className="w-12 h-12 text-lg" />
              </InputOTPGroup>
            </InputOTP>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <Button
          type="submit"
          disabled={isLoading || firstAdminOtp.length !== 6}
          className="w-full h-12 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-300 shadow-lg hover:shadow-xl group"
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              <span>{t("admin.loading.creating")}</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="h-4 w-4 group-hover:scale-110 transition-transform" />
              <span>{t("admin.setup.verifyButton")}</span>
            </div>
          )}
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setAuthStep("first-admin-setup");
            setFirstAdminOtp("");
          }}
          className="w-full h-10 group"
        >
          <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          {t("admin.form.back")}
        </Button>
      </div>
    </form>
  );

  const renderCredentialsForm = () => (
    <form onSubmit={handleCredentialsSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label
            htmlFor="email"
            className="flex items-center gap-2 text-sm font-medium"
          >
            <Mail className="h-4 w-4" />
            {t("admin.form.email")}
          </Label>
          <Input
            id="email"
            type="email"
            value={credentials.email}
            onChange={(e) =>
              setCredentials((prev) => ({ ...prev, email: e.target.value }))
            }
            placeholder={t("admin.form.placeholder.email")}
            className="h-12 transition-all duration-200 focus:scale-[1.02]"
            required
          />
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="password"
            className="flex items-center gap-2 text-sm font-medium"
          >
            <Lock className="h-4 w-4" />
            {t("admin.form.password")}
          </Label>
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={credentials.password}
              onChange={(e) =>
                setCredentials((prev) => ({
                  ...prev,
                  password: e.target.value,
                }))
              }
              placeholder={t("admin.form.placeholder.password")}
              className="h-12 pr-10 transition-all duration-200 focus:scale-[1.02]"
              required
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <Button
          type="submit"
          disabled={isLoading}
          className="w-full h-12 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-300 shadow-lg hover:shadow-xl group"
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              <span>{t("admin.loading.verifying")}</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <Shield className="h-4 w-4 group-hover:scale-110 transition-transform" />
              <span>{t("admin.form.verifyCredentials")}</span>
            </div>
          )}
        </Button>

        <Button
          type="button"
          variant="ghost"
          onClick={() => setAuthStep("forgot-password")}
          className="w-full h-10 text-muted-foreground hover:text-primary transition-colors duration-200"
        >
          {t("admin.form.forgotPasswordLink")}
        </Button>
      </div>
    </form>
  );

  const renderOTPForm = () => (
    <form onSubmit={handleOTPSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            {t("admin.otp.checkEmail", { email: currentEmail })}
          </p>
        </div>

        <div className="space-y-2">
          <Label
            htmlFor="otp"
            className="flex items-center justify-center gap-2 text-sm font-medium"
          >
            <Lock className="h-4 w-4" />
            {t("admin.form.verificationCode")}
          </Label>
          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={otpCode}
              onChange={setOtpCode}
              className="gap-2"
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
              </InputOTPGroup>
              <InputOTPSeparator />
              <InputOTPGroup>
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <Button
          type="submit"
          disabled={isLoading || otpCode.length !== 6}
          className="w-full h-12 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-300 shadow-lg hover:shadow-xl group"
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
              <span>{t("admin.loading.verifying")}</span>
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="h-4 w-4 group-hover:scale-110 transition-transform" />
              <span>{t("admin.otp.loginButton")}</span>
            </div>
          )}
        </Button>

        <Button
          type="button"
          variant="outline"
          onClick={resetToCredentials}
          className="w-full h-10 group"
        >
          <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
          {t("admin.otp.restartButton")}
        </Button>
      </div>
    </form>
  );

  const renderForgotPasswordForm = () => (
    <div className="space-y-6">
      <Formik
        initialValues={{ email: forgotEmail }}
        validationSchema={forgotPasswordSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            setSubmitting(true);
            await handleForgotPasswordSubmit(values.email);
          } catch (error) {
            console.error("Error in forgot password form:", error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          touched,
          isValid,
          dirty,
          isSubmitting: formikSubmitting,
        }) => (
          <Form className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="flex items-center gap-2 text-sm font-medium"
              >
                <Mail className="h-4 w-4" />
                {t("admin.form.email")}
              </Label>
              <Field
                as={Input}
                id="email"
                name="email"
                type="email"
                placeholder={t("admin.form.placeholder.email")}
                className={`h-12 transition-all duration-200 focus:scale-[1.02] ${
                  errors.email && touched.email ? "border-red-500" : ""
                }`}
              />
              <ErrorMessage
                name="email"
                component="div"
                className="text-red-500 text-xs mt-1"
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading || formikSubmitting || !isValid || !dirty}
              className="w-full h-12 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-300 shadow-lg hover:shadow-xl group"
            >
              {isLoading || formikSubmitting ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                  <span>{t("admin.loading.resetting")}</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <Mail className="h-4 w-4 group-hover:scale-110 transition-transform" />
                  <span>{t("admin.forgotPassword.resetButton")}</span>
                </div>
              )}
            </Button>
          </Form>
        )}
      </Formik>

      <Button
        type="button"
        variant="outline"
        onClick={resetToCredentials}
        className="w-full h-10 group"
      >
        <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
        {t("admin.forgotPassword.backButton")}
      </Button>
    </div>
  );

  const renderResetOTPForm = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            {t("admin.otp.checkEmail", { email: resetEmail })}
          </p>
        </div>

        <form onSubmit={handleResetOTPSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="resetOtp"
              className="flex items-center justify-center gap-2 text-sm font-medium"
            >
              <Lock className="h-4 w-4" />
              {t("admin.form.verificationCode")}
            </Label>
            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={resetOtp}
                onChange={setResetOtp}
                className="gap-2"
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} className="w-12 h-12 text-lg" />
                  <InputOTPSlot index={1} className="w-12 h-12 text-lg" />
                  <InputOTPSlot index={2} className="w-12 h-12 text-lg" />
                  <InputOTPSlot index={3} className="w-12 h-12 text-lg" />
                  <InputOTPSlot index={4} className="w-12 h-12 text-lg" />
                  <InputOTPSlot index={5} className="w-12 h-12 text-lg" />
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading || resetOtp.length !== 6}
            className="w-full h-12 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-300 shadow-lg hover:shadow-xl group"
          >
            {isLoading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                <span>{t("admin.loading.verifying")}</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <CheckCircle className="h-4 w-4 group-hover:scale-110 transition-transform" />
                <span>{t("admin.resetOtp.continueButton")}</span>
              </div>
            )}
          </Button>
        </form>
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={resetToCredentials}
        className="w-full h-10 group"
      >
        <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform" />
        {t("admin.resetOtp.restartButton")}
      </Button>
    </div>
  );

  const renderNewPasswordForm = () => (
    <Formik
      initialValues={{
        newPassword: newPassword,
        confirmPassword: confirmPassword,
      }}
      validationSchema={newPasswordSchema}
      onSubmit={async (values, { setSubmitting }) => {
        try {
          setSubmitting(true);
          await handleNewPasswordSubmit(
            values.newPassword,
            values.confirmPassword,
          );
        } catch (error) {
          console.error("Error in new password form:", error);
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        errors,
        touched,
        isValid,
        dirty,
        isSubmitting: formikSubmitting,
      }) => (
        <Form className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="newPassword"
                className="flex items-center gap-2 text-sm font-medium"
              >
                <Lock className="h-4 w-4" />
                {t("admin.form.newPassword")}
              </Label>
              <div className="relative">
                <Field
                  as={Input}
                  id="newPassword"
                  name="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  placeholder={t("admin.form.placeholder.password")}
                  className={`h-12 pr-10 transition-all duration-200 focus:scale-[1.02] ${
                    errors.newPassword && touched.newPassword
                      ? "border-red-500"
                      : ""
                  }`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              <ErrorMessage
                name="newPassword"
                component="div"
                className="text-red-500 text-xs mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum 8 caractères avec majuscule, minuscule et chiffre
              </p>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="confirmPassword"
                className="flex items-center gap-2 text-sm font-medium"
              >
                <Lock className="h-4 w-4" />
                {t("admin.form.confirmPassword")}
              </Label>
              <div className="relative">
                <Field
                  as={Input}
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder={t("admin.form.placeholder.password")}
                  className={`h-12 pr-10 transition-all duration-200 focus:scale-[1.02] ${
                    errors.confirmPassword && touched.confirmPassword
                      ? "border-red-500"
                      : ""
                  }`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              <ErrorMessage
                name="confirmPassword"
                component="div"
                className="text-red-500 text-xs mt-1"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading || formikSubmitting || !isValid || !dirty}
            className="w-full h-12 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-300 shadow-lg hover:shadow-xl group"
          >
            {isLoading || formikSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                <span>{t("admin.loading.resetting")}</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <CheckCircle className="h-4 w-4 group-hover:scale-110 transition-transform" />
                <span>{t("admin.newPassword.resetButton")}</span>
              </div>
            )}
          </Button>
        </Form>
      )}
    </Formik>
  );

  return (
    <div className="min-h-screen  ">
      <div className="relative z-10 flex min-h-screen items-center justify-center p-4 sm:p-6 lg:p-8">
        <Card className="w-full max-w-lg glass-effect border-0 shadow-2xl">
          <CardHeader className="text-center space-y-4 pb-8">
            <div className="flex justify-center">
              <div className="p-4 bg-primary/10 rounded-full">
                <Shield className="h-8 w-8 text-primary" />
              </div>
            </div>
            <div className="space-y-2">
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                {getStepTitle()}
              </CardTitle>
              <CardDescription className="text-base">
                {getStepDescription()}
              </CardDescription>
            </div>
          </CardHeader>

          <CardContent className="px-8 pb-8">
            <div className="space-y-6">
              {authStep === "first-admin-setup" && renderFirstAdminSetupForm()}
              {authStep === "first-admin-otp" && renderFirstAdminOTPForm()}
              {authStep === "credentials" && renderCredentialsForm()}
              {authStep === "otp" && renderOTPForm()}
              {authStep === "forgot-password" && renderForgotPasswordForm()}
              {authStep === "reset-otp" && renderResetOTPForm()}
              {authStep === "new-password" && renderNewPasswordForm()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Animations CSS */}
      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>

      {/* Contrôles sticky theme et langue */}
      <AuthStickyControls />
    </div>
  );
};

export default AdminLogin;
